<template>
    <div class="page-wrap-contents">
        <h2 class="title">포인트 지급/회수 요청내역</h2>

        <!-- 검색 조건 Form -->
        <PointRequestHistorySearchForm
            @initForm="initForm"
            @submit="searchHandler"
        />

        <!-- 검색 결과 List -->
        <PointRequestHistorySearchResult
            :data="tableData.content"
            :pagination="{
                page: pageIndexRef, // 현재 페이지
                pageSize: pageSizeRef, // 한 페이지당 보이는 데이터 수
                pageCount: tableData.totalPages, // 총 페이지 수
                itemCount: tableData.totalElements, // 총 데이터 수
            }"
            :isLoading="isLoading"
            @onPageIndexChange="onPageIndexChange"
            @onPageSizeChange="onPageSizeChange"
        />
        <HelperNotice :blocks="helperBlock" />
    </div>
</template>

<script lang="ts">
export const initialValues = {
    periodType: PeriodTypeE.REGISTER_DATE,
    fromDate: DateTime.fromMillis(
        new Date().setMonth(new Date().getMonth() - 1),
    ).toFormat('yyyy-MM-dd'), // FIXME: Luxon, valina JS Date 혼용됨. 수정 필요 (한달전 날짜를 계산할 때, DateTime.now.minus({months: 1})를 사용하면 2023-07-01 ~ 2023-07-31이 아니라 2023-06-30 ~ 2023-07-31이 됨)
    toDate: DateTime.now().toFormat('yyyy-MM-dd'),
    provideType: 'ALL',
    provideStatus: 'ALL',
} as PointRequestHistorySearchFormStateI;
</script>

<script setup lang="ts">
import {getPointRequestHistory as getPointRequestHistoryApi} from '@/api/points/point-request-history-api';
import HelperNotice from '@/components/common/paragraph/HelperNotice.vue';
import PointRequestHistorySearchForm from '@/components/points/request-history/PointRequestHistorySearchForm.vue';
import PointRequestHistorySearchResult from '@/components/points/request-history/PointRequestHistorySearchResult.vue';
import {PointRequestHistoryParamsT} from '@/types/points/api/point-request-history-api-types';
import {PeriodTypeE} from '@/types/points/point-keyword-types';
import {
    PointRequestHistoryRouteI,
    PointRequestHistorySearchFormStateI,
    PointRequestHistoryTableDataI,
} from '@/types/points/point-request-history-types';
import {AxiosError} from 'axios';
import {DateTime} from 'luxon';
import {useForm} from 'vee-validate';
import {onMounted, reactive, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';

// props, emits, libs
const {
    values: formState,
    setFieldValue,
    resetForm,
    handleSubmit,
} = useForm({
    initialValues,
});

const route = useRoute() as unknown as PointRequestHistoryRouteI;
const router = useRouter();

// ref, reactive
const isLoading = ref(false);

let tableData: PointRequestHistoryTableDataI = reactive({
    content: [],
    totalElements: 0,
    totalPages: 0,
});

const pageIndexRef = ref(1);
const pageSizeRef = ref(10);

// methods
const initForm = () => {
    router.replace({
        path: route.path,
    });

    resetForm();
};

const onPageIndexChange = (currentPageIndex: number) => {
    getPointRequestHistoryData(currentPageIndex);
};

const onPageSizeChange = (pageSize: number) => {
    pageSizeRef.value = pageSize;
};

/**
 * @description 검색 버튼 클릭 시, vee-validate 체크 & API 요청 실행
 */
const searchHandler = handleSubmit(() => getPointRequestHistoryData(1));

/**
 * @description API 요청 (pageIndex는 argument값에 따라 다름)
 */
const getPointRequestHistoryData = async (pageIndex: number) => {
    pageIndexRef.value = pageIndex;

    router.replace({
        path: route.path,
        query: {
            periodType: formState.periodType,
            fromDate: formState.fromDate,
            toDate: formState.toDate,
            provideType: formState.provideType,
            provideStatus: formState.provideStatus,
            page: pageIndexRef.value,
            size: pageSizeRef.value,
        },
    });

    try {
        isLoading.value = true;

        const params: PointRequestHistoryParamsT = {
            periodType: formState.periodType,
            fromDate: formState.fromDate,
            toDate: formState.toDate,
            ...(formState.provideType !== 'ALL' && {
                provideType: formState.provideType,
            }),
            ...(formState.provideStatus !== 'ALL' && {
                provideStatus: formState.provideStatus,
            }),
            page: pageIndexRef.value - 1, // index값 사용
            size: pageSizeRef.value,
        };

        const {content, totalElements, totalPages} =
            await getPointRequestHistoryApi(params);

        tableData.content = content;
        tableData.totalElements = totalElements;
        tableData.totalPages = totalPages;
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);
        window.customAlert(axiosError.message);
    } finally {
        isLoading.value = false;
    }
};

const helperBlock = [
    {
        title: '도움말',
        items: [
            '처리상태 열의 상태 Badge를 클릭하면 요청의 상세내용을 확인할 수 있습니다.',
            '요청대기 상태에만 요청 수정 및 취소가 가능합니다.',
            '요청건수, 가입대기건수, 성공건수, 실패건수 열의 각 데이터를 클릭하면, 해당 데이터의 상세내역 리스트로 이동합니다.',
        ],
    },
];

onMounted(() => {
    // NOTE: "올바른" 쿼리스트링 값대로 formState 저장
    const query = route.query;

    if (Number(query.size)) {
        if ([10, 30, 50, 100].includes(Number(query.size))) {
            pageSizeRef.value = Number(query.size);
        } else {
            pageSizeRef.value = 10;
        }
    }

    if (!!query.fromDate && !!query.toDate) {
        if (
            !DateTime.fromFormat(query.fromDate, 'yyyy-MM-dd').isValid ||
            !DateTime.fromFormat(query.toDate, 'yyyy-MM-dd').isValid
        ) {
            // NOTE: 날짜가 올바른 형식의 날짜이지 않을때에는 route 이동X
            alert(
                '올바른 날짜가 아닙니다. yyyy-MM-dd 형식의 실존하는 날짜로 접근해 주세요.',
            );
            router.back();
            return;
        }

        if (
            DateTime.fromFormat(query.fromDate, 'yyyy-MM-dd').toMillis() >
            DateTime.fromFormat(query.toDate, 'yyyy-MM-dd').toMillis()
        ) {
            // NOTE: fromDate가 toDate보다 미래날짜인 경우에는 route 이동X
            alert(
                `fromDate가 toDate보다 미래 날짜입니다.
toDate와 동일한 날짜 혹은 toDate보다 과거 날짜로 접근해 주세요.`,
            );
            router.back();
            return;
        }

        setFieldValue('fromDate', query.fromDate);
        setFieldValue('toDate', query.toDate);
    }

    if (Number(query.page)) {
        // NOTE: 숫자일 때, 페이지 첫 방문

        if (Number(query.page) > 0) {
            // NOTE: 1이상일때
            pageIndexRef.value = Number(query.page);
        } else {
            // NOTE: 0이거나 음수일 때
            pageIndexRef.value = 1;
        }
    } else if (typeof query.page === 'undefined') {
        pageIndexRef.value = 1;
    } else {
        return window.customAlert(
            'page 값이 올바르지 않습니다. page 번호는 숫자로만 입력해 주세요.',
            () => router.back(),
        );
    }

    // TODO: 그외 state의 값이 formState의 프로퍼티이고, 값에 해당될때에만 저장하는 로직 추가하기. 값이 ALL일땐 저장X

    getPointRequestHistoryData(pageIndexRef.value);
});
</script>

<style lang="scss" scoped></style>
